import { memo } from "react";
import { AlertCircle, Bell, Cake, CheckCircle2, Clock, Gem, HeartHandshake, Trash2, UserPlus } from "lucide-react";

const TYPE_META = {
  visit_overdue:     { Icon: AlertCircle,    bg: "bg-red-50 dark:bg-red-900/20",       color: "text-red-500 dark:text-red-400"       },
  visit_today:       { Icon: Clock,          bg: "bg-amber-50 dark:bg-amber-900/20",   color: "text-amber-500 dark:text-amber-400"   },
  prayer_active:     { Icon: HeartHandshake, bg: "bg-rose-50 dark:bg-rose-900/20",     color: "text-rose-500 dark:text-rose-400"     },
  birthday_today:    { Icon: Cake,           bg: "bg-pink-50 dark:bg-pink-900/20",     color: "text-pink-500 dark:text-pink-400"     },
  anniversary_today: { Icon: Gem,            bg: "bg-violet-50 dark:bg-violet-900/20", color: "text-violet-500 dark:text-violet-400" },
  access_request:    { Icon: UserPlus,       bg: "bg-blue-50 dark:bg-blue-900/20",     color: "text-blue-600 dark:text-blue-400"     },
};

function NotifSkeleton() {
  return (
    <div className="animate-pulse px-4 py-3.5 border-b border-slate-100 dark:border-slate-700/50 flex items-start gap-3">
      <div className="h-9 w-9 rounded-[10px] bg-slate-100 dark:bg-slate-700 shrink-0" />
      <div className="flex-1 space-y-1.5 pt-0.5">
        <div className="h-3 w-2/3 bg-slate-100 dark:bg-slate-700 rounded" />
        <div className="h-2.5 w-1/2 bg-slate-100 dark:bg-slate-700 rounded" />
      </div>
    </div>
  );
}

// onNavigate(id, route) — navigates and marks the notification as read
const NotifRow = memo(function NotifRow({ item, onNavigate }) {
  const meta = TYPE_META[item.type] ?? { Icon: Bell, bg: "bg-slate-50 dark:bg-slate-700/50", color: "text-slate-400 dark:text-slate-500" };
  const Icon = meta.Icon;

  return (
    <button
      onClick={() => onNavigate(item.id, item.route)}
      className={`
        relative w-full flex items-start gap-3 px-4 py-3.5 text-left
        border-b border-slate-100 dark:border-slate-700/50 last:border-0
        transition-colors duration-150
        ${item.read ? "opacity-50" : ""}
        hover:bg-slate-50/60 dark:hover:bg-slate-700/30 active:bg-slate-50 dark:active:bg-slate-700/40
      `}
    >
      {!item.read && (
        <span className="absolute left-2.5 top-4 h-1.5 w-1.5 rounded-full bg-blue-500" />
      )}

      <div className={`h-9 w-9 rounded-[10px] flex items-center justify-center shrink-0 ${meta.bg}`}>
        <Icon size={15} className={meta.color} strokeWidth={2} />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold text-slate-800 dark:text-slate-100 leading-snug">
          {item.title}
        </p>
        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-none truncate">
          {item.body}
        </p>
      </div>
    </button>
  );
});

export default function NotificationsPanel({ notifications, accessRequests = [], loading, onDismissAll, onClear, onNavigate }) {
  const accessItems = accessRequests.map((r) => ({
    id: `access_request_${r.id}`,
    type: "access_request",
    title: "Nueva solicitud de acceso",
    body: `${r.name || "Sin nombre"} · ${r.email}`,
    route: "/admin/users",
    read: false,
  }));

  const allItems = [...accessItems, ...notifications];
  const unread = allItems.filter((n) => !n.read).length;

  return (
    <div className="flex flex-col h-full">

      {/* Actions bar */}
      {!loading && allItems.length > 0 && (
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 dark:border-slate-700/50">
          <p className="text-[11px] text-slate-400 dark:text-slate-500">
            {unread > 0 ? `${unread} sin leer` : "Todo leído"}
          </p>
          <div className="flex gap-3">
            {unread > 0 && (
              <button
                onClick={onDismissAll}
                className="flex items-center gap-1 text-[11px] font-semibold text-blue-600 dark:text-blue-400 active:opacity-70"
              >
                <CheckCircle2 size={12} />
                Marcar leídas
              </button>
            )}
            <button
              onClick={onClear}
              className="flex items-center gap-1 text-[11px] font-semibold text-slate-400 dark:text-slate-500 active:opacity-70"
            >
              <Trash2 size={11} />
              Limpiar
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div>
            {[1, 2, 3].map((i) => <NotifSkeleton key={i} />)}
          </div>
        ) : allItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-16 px-6 text-center">
            <div className="h-14 w-14 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center mb-3">
              <Bell size={22} className="text-slate-300 dark:text-slate-600" />
            </div>
            <p className="text-[15px] font-semibold text-slate-600 dark:text-slate-300">Sin notificaciones</p>
            <p className="text-[12px] text-slate-400 dark:text-slate-500 mt-1 leading-relaxed">
              Los recordatorios de visitas y seguimientos aparecerán aquí.
            </p>
          </div>
        ) : (
          <div className="relative">
            {allItems.map((n) => (
              <NotifRow key={n.id} item={n} onNavigate={onNavigate} />
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
